import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getOfferings,
  getOfferingById,
  createOffering,
  saveOffering,
  reviseOffering,
  submitOffering,
  actApproval,
  sendOffering,
  acceptOffering,
  declineOffering,
  cancelOffering,
  convertOffering,
  setOfferingRevenueScheme,
} from "libs/api/offering";

const thunk = (name, apiFn) =>
  createAsyncThunk(`offering/${name}`, async ({ cbPending, cbFulfilled, cbRejected, cbFinally, ...args } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await apiFn(args);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      const err = error.response ? error.response.data : { message: error.message };
      typeof cbRejected === "function" && cbRejected(err);
      throw JSON.stringify(err);
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  });

export const getOfferingsThunk = createAsyncThunk(
  "offering/getAll",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, queryParams } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const res = await getOfferings(queryParams);
      typeof cbFulfilled === "function" && cbFulfilled();
      return res?.data;
    } catch (error) {
      const err = error.response ? error.response.data : { message: error.message };
      typeof cbRejected === "function" && cbRejected(err);
      throw JSON.stringify(err);
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

export const getOfferingByIdThunk = createAsyncThunk(
  "offering/getById",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const res = await getOfferingById(id);
      typeof cbFulfilled === "function" && cbFulfilled();
      return res?.data;
    } catch (error) {
      const err = error.response ? error.response.data : { message: error.message };
      typeof cbRejected === "function" && cbRejected(err);
      throw JSON.stringify(err);
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

export const createOfferingThunk = createAsyncThunk(
  "offering/create",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, body } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const res = await createOffering(body);
      typeof cbFulfilled === "function" && cbFulfilled(res?.data);
      return res?.data;
    } catch (error) {
      const err = error.response ? error.response.data : { message: error.message };
      typeof cbRejected === "function" && cbRejected(err);
      throw JSON.stringify(err);
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

export const reviseOfferingThunk = createAsyncThunk(
  "offering/revise",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id, body } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const res = await reviseOffering(id, body);
      typeof cbFulfilled === "function" && cbFulfilled(res?.data);
      return res?.data;
    } catch (error) {
      const err = error.response ? error.response.data : { message: error.message };
      typeof cbRejected === "function" && cbRejected(err);
      throw JSON.stringify(err);
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

const makeTransitionThunk = (name, apiFn) =>
  createAsyncThunk(`offering/${name}`, async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id, body } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const res = await apiFn(id, body);
      typeof cbFulfilled === "function" && cbFulfilled(res?.data);
      return res?.data;
    } catch (error) {
      const err = error.response ? error.response.data : { message: error.message };
      typeof cbRejected === "function" && cbRejected(err);
      throw JSON.stringify(err);
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  });

export const saveOfferingThunk = makeTransitionThunk("save", saveOffering);
export const submitOfferingThunk = makeTransitionThunk("submit", submitOffering);
export const actApprovalThunk = makeTransitionThunk("actApproval", actApproval);
export const sendOfferingThunk = makeTransitionThunk("send", sendOffering);
export const acceptOfferingThunk = makeTransitionThunk("accept", acceptOffering);
export const declineOfferingThunk = makeTransitionThunk("decline", declineOffering);
export const cancelOfferingThunk = makeTransitionThunk("cancel", cancelOffering);
export const convertOfferingThunk = makeTransitionThunk("convert", convertOffering);
export const setOfferingRevenueSchemeThunk = makeTransitionThunk("setRevenueScheme", setOfferingRevenueScheme);
